import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "react-router";

import { logout } from "../../api/auth";
import type { Me } from "../../api/auth";
import { errorText } from "../../api/client";
import { newRecoveryCodes, updateMe } from "../../api/me";
import type { MeUpdate, Profile, Transport } from "../../api/me";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { EmptyState } from "../../components/EmptyState";
import { Field, FormError } from "../../components/Field";
import { Skeleton } from "../../components/Skeleton";
import { useToast } from "../../components/Toast";
import { nl } from "../../i18n/nl";
import { ME_KEY, useMe } from "../auth/session";
import { DeleteAccount } from "./DeleteAccount";
import { DistancePicker, MunicipalityPicker, NotifySwitches, ProfilePicker, TransportPicker } from "./PreferenceFields";
import type { NotifyKey } from "./PreferenceFields";
import styles from "./profile.module.css";

const t = nl.profile;

interface Preferences {
  profiles: Profile[];
  home_municipality: string | null;
  max_distance_km: number | null;
  transport: Transport | null;
  initials: string;
  notify: Record<NotifyKey, boolean>;
}

function preferencesOf(me: Me): Preferences {
  return {
    profiles: me.profiles,
    home_municipality: me.home_municipality,
    max_distance_km: me.max_distance_km,
    transport: me.transport,
    initials: me.initials,
    notify: { claim: true, wait: true, follow: true, digest: true, email: false, ...me.notify },
  };
}

function PreferencesForm({ me }: { me: Me }) {
  const client = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState(() => preferencesOf(me));
  const [profileError, setProfileError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (patch: MeUpdate) => updateMe(patch),
    onSuccess: (updated) => {
      client.setQueryData(ME_KEY, updated);
      setDraft(preferencesOf(updated));
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (draft.profiles.length === 0) {
      setProfileError(nl.prefs.profilesRequired);
      return;
    }
    setProfileError(null);
    const previous = preferencesOf(me);
    save.mutate(
      { ...draft, initials: draft.initials || null },
      {
        onSuccess: () => {
          toast({
            message: t.saved,
            onUndo: () => {
              save.mutate({ ...previous, initials: previous.initials || null });
            },
          });
        },
      },
    );
  }

  return (
    <form className={styles.card} onSubmit={submit} noValidate aria-labelledby="prefs-title">
      <h2 id="prefs-title" className={styles.sectionTitle}>
        {t.preferences}
      </h2>
      <ProfilePicker
        value={draft.profiles}
        onChange={(profiles) => {
          setDraft({ ...draft, profiles });
        }}
        error={profileError}
      />
      <MunicipalityPicker
        value={draft.home_municipality}
        onChange={(home_municipality) => {
          setDraft({ ...draft, home_municipality });
        }}
      />
      <DistancePicker
        value={draft.max_distance_km}
        onChange={(max_distance_km) => {
          setDraft({ ...draft, max_distance_km });
        }}
      />
      <TransportPicker
        value={draft.transport}
        onChange={(transport) => {
          setDraft({ ...draft, transport });
        }}
      />
      <NotifySwitches
        value={draft.notify}
        onChange={(notify) => {
          setDraft({ ...draft, notify });
        }}
      />
      <Field
        label={t.initials}
        help={t.initialsHelp}
        maxLength={3}
        autoCapitalize="characters"
        value={draft.initials}
        onChange={(event) => {
          setDraft({ ...draft, initials: event.target.value.replace(/[^\p{L}]/gu, "").toUpperCase() });
        }}
      />
      <FormError message={save.error ? errorText(save.error) : null} />
      <div>
        <Button type="submit" variant="primary" disabled={save.isPending}>
          {save.isPending ? nl.auth.working : t.save}
        </Button>
      </div>
    </form>
  );
}

function AccountSection({ me }: { me: Me }) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const [codes, setCodes] = useState<string[] | null>(null);
  const issue = useMutation({
    mutationFn: newRecoveryCodes,
    onSuccess: (fresh) => {
      setCodes(fresh);
      void client.invalidateQueries({ queryKey: ME_KEY });
    },
  });
  const signOut = useMutation({
    mutationFn: logout,
    onSettled: () => {
      client.clear();
      void navigate("/over", { replace: true });
    },
  });

  return (
    <section className={styles.card} aria-labelledby="account-title">
      <h2 id="account-title" className={styles.sectionTitle}>
        {t.account}
      </h2>
      <dl className={styles.facts}>
        <div>
          <dt>{t.email}</dt>
          <dd>{me.email}</dd>
        </div>
        <div>
          <dt>{t.recoveryTitle}</dt>
          <dd>{t.recoveryLeft(me.recovery_codes_left)}</dd>
        </div>
      </dl>
      <p className={styles.help}>{t.recoveryNewText}</p>
      <FormError message={issue.error ? errorText(issue.error) : null} />
      <div className={styles.row}>
        <Button
          disabled={issue.isPending}
          onClick={() => {
            issue.mutate();
          }}
        >
          {t.recoveryNew}
        </Button>
        <Button
          variant="ghost"
          disabled={signOut.isPending}
          onClick={() => {
            signOut.mutate();
          }}
        >
          {t.logout}
        </Button>
      </div>
      <Dialog
        open={codes !== null}
        onOpenChange={(open) => {
          if (!open) setCodes(null);
        }}
        title={nl.auth.codes.title}
        description={nl.auth.codes.text}
      >
        <ul className={styles.codes} aria-label={nl.auth.codes.title}>
          {codes?.map((code) => <li key={code}>{code}</li>)}
        </ul>
        <div className={styles.row}>
          <Button
            onClick={() => {
              void navigator.clipboard.writeText((codes ?? []).join("\n"));
            }}
          >
            {nl.auth.codes.copy}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setCodes(null);
            }}
          >
            {t.recoveryDone}
          </Button>
        </div>
      </Dialog>
    </section>
  );
}

export function ProfilePage() {
  const me = useMe();
  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{nl.screens.profile}</h1>
      {me.isPending ? (
        <div className={styles.card} role="status" aria-busy="true" aria-label={nl.common.loading}>
          <Skeleton height={24} width="40%" />
          <Skeleton height={120} />
        </div>
      ) : me.data ? (
        <>
          <PreferencesForm me={me.data} />
          <AccountSection me={me.data} />
          <DeleteAccount email={me.data.email} />
        </>
      ) : (
        <EmptyState
          title={t.loadError}
          action={<Button onClick={() => void me.refetch()}>{nl.common.retry}</Button>}
        />
      )}
    </div>
  );
}
